import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type { ReactNode } from 'react';

import {
  createAIConversation,
  fetchConversationMessagesById,
  openChat,
  sendLlmMessage,
  setCurrentConversationId,
  setPlatformSpecificTools,
  setPlatformSpecificToolsHandlers,
} from 'app-components/flux/ai-chat';
import * as aiChatSelectors from 'app-components/flux/ai-chat/aiChatSelectors';
import type { AiChatHiddenContext, ConversationItem } from 'app-components/flux/ai-chat/aiChatTypes';
import { getLogger } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import { clearQueryCache } from 'app-components/flux/ai-chat/tools/handlers/reports';
import { buildWebToolHandlers, buildWebTools } from 'components/AIChat/llmWebTools';

const log = getLogger('ChatSession');

const EMPTY_MESSAGES: ConversationItem[] = [];

type SendMessageOptions = {
  trackSend?: boolean;
  hiddenContext?: AiChatHiddenContext;
  enableWebSearch?: boolean;
};

/**
 * Web AI chat session state + actions (provider-backed).
 *
 * Key behavior (kept in sync with mobile):
 * - Do NOT create a backend conversation when the UI opens.
 * - Create the conversation lazily on first send, so we don't generate empty conversations.
 * - "New chat" clears the current conversation id; the next send will create a fresh conversation.
 */
type ChatSessionValue = {
  conversationId: string | null;
  messages: ConversationItem[];
  streamingMessage: string;
  reasoningMessage: string | null;
  isConversationLoading: boolean;
  sendMessage: (text: string, options?: SendMessageOptions) => Promise<void>;
  /**
   * Resets the session to a "new chat" state (no active conversation selected).
   * The backend conversation is created lazily on the next `sendMessage()`.
   */
  startNewChat: () => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  isReady: boolean;
  error: string | null;
};

const ChatSessionContext = createContext<ChatSessionValue | null>(null);

type ChatSessionProviderProps = {
  children: ReactNode;
};

export const ChatSessionProvider = ({ children }: ChatSessionProviderProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const hasInitialized = useRef(false);
  const conversationIdRef = useRef<string | null>(null);

  /**
   * Prevent duplicate POSTs when multiple sends happen quickly while there is no conversation yet
   * (e.g., pressing enter + clicking send, or "autoSend" on open).
   */
  const inFlightCreateRef = useRef<Promise<string | null> | null>(null);

  /**
   * Local send/create error surfaced through the session `error` field so the UI can show it
   * even when there is not yet a `conversationId` (and therefore no per-conversation error state).
   */
  const [sendError, setSendError] = useState<string | null>(null);

  const chatUIState = useAppSelector(aiChatSelectors.getChatUIState);
  const conversationId = useAppSelector(aiChatSelectors.getCurrentConversationId);
  conversationIdRef.current = conversationId;

  const conversationData = useAppSelector((state) =>
    conversationId ? aiChatSelectors.selectConversation(state, conversationId) : null,
  );

  const messages = conversationData?.messages ?? EMPTY_MESSAGES;
  const streamingMessage = conversationData?.streamingMessage ?? '';
  const reasoningMessage = conversationData?.reasoningMessage ?? null;
  const isLoading = conversationData?.isLoading ?? false;
  const error = conversationData?.error ?? null;

  useEffect(() => {
    if (!conversationId) {
      // If a "new chat" resets the id, allow future sends to create a fresh conversation.
      inFlightCreateRef.current = null;
    }
  }, [conversationId]);

  useEffect(() => {
    dispatch(setPlatformSpecificTools(buildWebTools()));
    dispatch(setPlatformSpecificToolsHandlers(buildWebToolHandlers()));
  }, [dispatch]);

  useEffect(() => {
    if (hasInitialized.current) return;

    if (chatUIState === 'minimized' && conversationId) {
      log.debug('ChatSession: Resuming minimized conversation', { conversationId });
      hasInitialized.current = true;
      dispatch(openChat());
      dispatch(fetchConversationMessagesById(conversationId));
    }
  }, [dispatch, chatUIState, conversationId]);

  const sendMessage = useCallback(
    async (text: string, options?: SendMessageOptions) => {
      setSendError(null);
      let activeId = conversationIdRef.current;

      if (!activeId) {
        // Lazy-create the conversation on first send to avoid empty conversations.
        if (!inFlightCreateRef.current) {
          inFlightCreateRef.current = dispatch(createAIConversation())
            .unwrap()
            .then((result: { id: string }) => {
              dispatch(setCurrentConversationId(result.id));
              return result.id;
            })
            .catch((err: unknown) => {
              log.error('ChatSession: Failed to create conversation for send', err);
              setSendError('Something went wrong. Please try again.');
              inFlightCreateRef.current = null;
              return null;
            });
        }

        activeId = await inFlightCreateRef.current;
        if (!activeId) return;
      }

      // Once we have an id, message dispatch is the same path as existing conversations.
      await dispatch(
        sendLlmMessage({
          conversationId: activeId,
          text,
          hiddenContext: options?.hiddenContext,
          trackSend: options?.trackSend,
          enableWebSearch: options?.enableWebSearch,
        }),
      ).unwrap();
    },
    [dispatch],
  );

  const startNewChat = useCallback(async (): Promise<void> => {
    setSendError(null);
    // IMPORTANT: this intentionally does not create a conversation.
    // The next call to `sendMessage()` will create a new backend conversation.
    // Also clear the ref immediately so a quick "new chat → send" can't race and send into the old conversation.
    const prevConversationId = conversationIdRef.current;
    conversationIdRef.current = null;
    inFlightCreateRef.current = null;
    dispatch(setCurrentConversationId(null));
    if (prevConversationId) clearQueryCache(prevConversationId);
  }, [dispatch]);

  const loadConversation = useCallback(
    async (convId: string): Promise<void> => {
      try {
        // Load history for a specific conversation and make it active.
        await dispatch(fetchConversationMessagesById(convId)).unwrap();
        dispatch(setCurrentConversationId(convId));
      } catch (err) {
        log.error('Failed to load conversation', err);
      }
    },
    [dispatch],
  );

  const value = useMemo(
    () => ({
      conversationId,
      messages,
      streamingMessage,
      reasoningMessage,
      isConversationLoading: isLoading,
      sendMessage,
      startNewChat,
      loadConversation,

      // "Ready to type/send" is based on loading state, not on presence of an id.
      // If there's no id yet, sendMessage() will create one.
      isReady: !isLoading,
      error: sendError ?? error,
    }),
    [
      conversationId,
      messages,
      streamingMessage,
      reasoningMessage,
      isLoading,
      sendMessage,
      startNewChat,
      loadConversation,
      error,
      sendError,
    ],
  );

  return <ChatSessionContext.Provider value={value}>{children}</ChatSessionContext.Provider>;
};

export const useChatSession = (): ChatSessionValue => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within ChatSessionProvider');
  }
  return context;
};
